import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import CategoryCascaderSelect from '@/shein-platform/components/CategoryCascaderSelect';
import SearchableSelect from '@/shein-platform/modules/products/components/SearchableSelect';
import SheinSwitch from '@/shein-platform/components/SheinSwitch';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';
import { extractSheinList, pickField } from '@/shein-platform/utils/sheinResponse';
import {
  AGENCY_AUTO_BIND_PREF_KEY,
  CERT_AUTO_BIND_ENABLED_PREF_KEY,
  PHOTO_AUTO_BIND_ENABLED_PREF_KEY,
  makeComplianceAutoBindRuleId,
  normalizeAgencyAutoBindRule,
  readCertAutoBindEnabled,
  readComplianceAutoBindRules,
  readPhotoAutoBindEnabled,
  saveComplianceAutoBindEnabled,
  saveComplianceAutoBindRules,
} from '@/shein-platform/modules/compliance/complianceAutoBindRules';
import {
  runAgencyAutoBind,
  runCertAutoBind,
  runPhotoAutoBind,
} from '@/shein-platform/modules/compliance/complianceAutoBindRunner';
import {
  AGENCY_TYPE_LABELS,
  agencyTypeLabel,
  isBindableAgency,
} from '@/shein-platform/modules/compliance/complianceDisplay';
import { showToast } from '@/utils/toast';

function agencyIdOf(row) {
  return pickField(row, 'agencyId', 'id', 'companyId');
}

function agencyNameOf(row) {
  return pickField(row, 'agencyName', 'companyName', 'name') || i18n.t('compliance.unnamed', { ns: 'workbench' });
}

function toastAutoBindSummary(summary, isPhoto) {
  const parts = [];
  if (summary.ok) parts.push(i18n.t('compliance.okCount', { ns: 'workbench', count: summary.ok }));
  if (summary.skip) parts.push(i18n.t('compliance.skipCount', { ns: 'workbench', count: summary.skip }));
  if (summary.fail) parts.push(i18n.t('compliance.failCount', { ns: 'workbench', count: summary.fail }));
  const doneLabel = i18n.t(isPhoto ? 'compliance.photoDone' : 'compliance.bindDone', { ns: 'workbench' });
  if (summary.errors?.length && !summary.ok && !summary.fail) {
    showToast(summary.errors[0], 'warning');
  } else if (summary.fail && summary.errors?.length) {
    showToast(`${parts.join('，')}。${summary.errors[0]}`, 'warning');
  } else if (summary.ok) {
    showToast(`${doneLabel}：${parts.join('，')}`, 'success');
  } else {
    showToast(parts.length ? parts.join('，') : i18n.t('compliance.nothing', { ns: 'workbench' }), 'info');
  }
}

/**
 * 合规自动补传规则：
 * - photo / certificate：全局开关 + 按货号款号匹配图库/证书库
 * - agency：仍为类目规则列表
 * @param {'photo'|'certificate'|'agency'} kind
 */
export default function ComplianceAutoBindRuleModal({
  open,
  shopId,
  kind = 'photo',
  busy = false,
  onClose,
  onRan,
}) {
  const { t } = useTranslation('workbench');
  const isPhoto = kind === 'photo';
  const isAgency = kind === 'agency';
  const isCert = kind === 'certificate';
  const isSimple = isPhoto || isCert;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  // —— photo / cert：开关 ——
  const [enabled, setEnabled] = useState(false);

  // —— agency：旧规则列表 ——
  const [categories, setCategories] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [draftCategoryId, setDraftCategoryId] = useState('');
  const [draftCategoryLabel, setDraftCategoryLabel] = useState('');
  const [draftProductTypeId, setDraftProductTypeId] = useState('');
  const [draftEnabled, setDraftEnabled] = useState(true);
  const [draftAgencyType, setDraftAgencyType] = useState('');
  const [draftAgencyId, setDraftAgencyId] = useState('');
  const [draftAgencyName, setDraftAgencyName] = useState('');

  const leafOptions = useMemo(() => flattenLeafCategories(categories), [categories]);

  const bindableAgencies = useMemo(
    () => (Array.isArray(agencies) ? agencies.filter(isBindableAgency) : []),
    [agencies],
  );

  const agencyTypeOptions = useMemo(() => {
    const map = new Map();
    bindableAgencies.forEach((row) => {
      const type = pickField(row, 'agencyType');
      if (type == null || type === '') return;
      const key = String(type);
      if (!map.has(key)) map.set(key, agencyTypeLabel(type));
    });
    Object.entries(AGENCY_TYPE_LABELS).forEach(([value, label]) => {
      if (!map.has(value)) map.set(value, label);
    });
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => Number(a.value) - Number(b.value));
  }, [bindableAgencies]);

  const agencyCompanyOptions = useMemo(() => {
    if (draftAgencyType === '') return [];
    return bindableAgencies
      .filter((row) => Number(pickField(row, 'agencyType')) === Number(draftAgencyType))
      .map((row) => ({
        value: String(agencyIdOf(row) ?? ''),
        label: agencyNameOf(row),
      }))
      .filter((opt) => opt.value);
  }, [bindableAgencies, draftAgencyType]);

  const resetDraft = useCallback(() => {
    setDraftCategoryId('');
    setDraftCategoryLabel('');
    setDraftProductTypeId('');
    setDraftEnabled(true);
    setDraftAgencyType('');
    setDraftAgencyId('');
    setDraftAgencyName('');
  }, []);

  useEffect(() => {
    if (!open) return;
    resetDraft();
  }, [open, shopId, kind, resetDraft]);

  useEffect(() => {
    if (!open || !shopId) {
      setCategories([]);
      setRules([]);
      setAgencies([]);
      setEnabled(false);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setLoading(true);
    const tasks = [sheinPlatformAPI.getPreferences().catch(() => null)];
    if (isAgency) {
      tasks.push(sheinPlatformAPI.getShopCategories(shopId).catch(() => null));
      tasks.push(sheinPlatformAPI.listComplianceAgencies(shopId, {
        pageNum: 1,
        pageSize: 100,
        bindableOnly: true,
      }).catch(() => null));
    }
    Promise.all(tasks)
      .then((results) => {
        if (cancelled) return;
        const prefRes = results[0];
        const prefs = prefRes?.preferences || prefRes || {};
        if (isPhoto) {
          setEnabled(readPhotoAutoBindEnabled(prefs, shopId));
        } else if (isCert) {
          setEnabled(readCertAutoBindEnabled(prefs, shopId));
        } else {
          setCategories(results[1]?.categories ?? results[1]?.items ?? results[1]?.list ?? results[1] ?? []);
          setRules(readComplianceAutoBindRules(
            prefs,
            AGENCY_AUTO_BIND_PREF_KEY,
            shopId,
            normalizeAgencyAutoBindRule,
          ));
          setAgencies(extractSheinList(results[2]));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [open, shopId, isPhoto, isAgency, isCert]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !running && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, running, busy, onClose]);

  const onCategoryChange = (value) => {
    const id = String(value || '').trim();
    setDraftCategoryId(id);
    if (id === '*') {
      setDraftCategoryLabel('全部类目');
      setDraftProductTypeId('');
      return;
    }
    const opt = leafOptions.find((o) => String(o.categoryId) === id);
    setDraftCategoryLabel(opt?.label || '');
    setDraftProductTypeId(opt?.productTypeId || '');
  };

  const addAgencyRule = () => {
    if (!draftCategoryId) {
      showToast(t('compliance.pickCategory'), 'warning');
      return;
    }
    if (!draftAgencyType || !draftAgencyId) {
      showToast(t('compliance.pickAgencyBoth'), 'warning');
      return;
    }
    const dupKey = rules.some((r) => (
      String(r.categoryId) === String(draftCategoryId)
      && String(r.agencyType) === String(draftAgencyType)
    ));
    if (dupKey) {
      showToast(t('compliance.dupRule'), 'warning');
      return;
    }
    const next = normalizeAgencyAutoBindRule({
      id: makeComplianceAutoBindRuleId('agency'),
      enabled: draftEnabled,
      categoryId: draftCategoryId,
      categoryPathLabel: draftCategoryLabel || (draftCategoryId === '*' ? '全部类目' : draftCategoryId),
      productTypeId: draftProductTypeId,
      agencyType: draftAgencyType,
      agencyId: draftAgencyId,
      agencyName: draftAgencyName,
    });
    if (!next) {
      showToast(t('compliance.badRule'), 'error');
      return;
    }
    setRules((prev) => [...prev, next]);
    resetDraft();
    showToast(t('compliance.addedRule'), 'info');
  };

  const removeRule = (id) => setRules((prev) => prev.filter((r) => r.id !== id));
  const toggleRule = (id, nextEnabled) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, enabled: nextEnabled } : r)));
  };

  const handleToggleEnabled = async (next) => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    setSaving(true);
    try {
      await saveComplianceAutoBindEnabled({
        getPreferences: () => sheinPlatformAPI.getPreferences(),
        updatePreferences: (body) => sheinPlatformAPI.updatePreferences(body),
        enabledKey: isPhoto ? PHOTO_AUTO_BIND_ENABLED_PREF_KEY : CERT_AUTO_BIND_ENABLED_PREF_KEY,
        shopId,
        enabled: next,
      });
      setEnabled(next);
      if (next) {
        setRunning(true);
        try {
          const summary = isPhoto
            ? await runPhotoAutoBind(shopId, { force: true })
            : await runCertAutoBind(shopId, { force: true });
          toastAutoBindSummary(summary, isPhoto);
          onRan?.(summary);
        } catch (e) {
          showToast(e?.message || t(isPhoto ? 'compliance.photoFail' : 'compliance.bindFail'), 'error');
        } finally {
          setRunning(false);
        }
      } else {
        showToast(t('compliance.closed'), 'info');
      }
    } catch (e) {
      showToast(e?.message || t('certLib.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRunOnce = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    setRunning(true);
    try {
      const summary = isPhoto
        ? await runPhotoAutoBind(shopId, { force: true })
        : await runCertAutoBind(shopId, { force: true });
      toastAutoBindSummary(summary, isPhoto);
      onRan?.(summary);
    } catch (e) {
      showToast(e?.message || t(isPhoto ? 'compliance.photoFail' : 'compliance.bindFail'), 'error');
    } finally {
      setRunning(false);
    }
  };

  const handleAgencySave = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    setSaving(true);
    try {
      await saveComplianceAutoBindRules({
        getPreferences: () => sheinPlatformAPI.getPreferences(),
        updatePreferences: (body) => sheinPlatformAPI.updatePreferences(body),
        prefKey: AGENCY_AUTO_BIND_PREF_KEY,
        shopId,
        rules,
        normalize: normalizeAgencyAutoBindRule,
      });
      showToast(t('compliance.saved'), 'success');
    } catch (e) {
      showToast(e?.message || t('certLib.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAgencyRun = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    setRunning(true);
    try {
      await saveComplianceAutoBindRules({
        getPreferences: () => sheinPlatformAPI.getPreferences(),
        updatePreferences: (body) => sheinPlatformAPI.updatePreferences(body),
        prefKey: AGENCY_AUTO_BIND_PREF_KEY,
        shopId,
        rules,
        normalize: normalizeAgencyAutoBindRule,
      });
      const summary = await runAgencyAutoBind(shopId);
      toastAutoBindSummary(summary, false);
      onRan?.(summary);
    } catch (e) {
      showToast(e?.message || t('compliance.bindFail'), 'error');
    } finally {
      setRunning(false);
    }
  };

  if (!open) return null;
  const disabled = busy || saving || running || loading;

  let title = t('compliance.titleCert');
  if (isPhoto) title = t('compliance.titlePhoto');
  if (isAgency) title = t('compliance.titleAgency');

  const simpleHint = isPhoto
    ? t('compliance.hintPhoto')
    : t('compliance.hintCert');

  return createPortal(
    <div className="spo-batch-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        disabled={disabled}
        onClick={() => !disabled && onClose?.()}
      />
      <div
        className="spo-batch-modal__panel spo-discuss-auto-rule-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-compliance-auto-bind-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-compliance-auto-bind-title">{title}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" disabled={disabled} onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>

        <div className="spo-batch-modal__body">
          {isSimple ? (
            <>
              <p className="spo-batch-modal__hint">{simpleHint}</p>
              {loading ? (
                <p className="spo-batch-modal__hint">{t('returnList.loading')}</p>
              ) : (
                <div className="spo-template-auto-rule__switch">
                  <SheinSwitch
                    checked={enabled}
                    disabled={disabled || !shopId}
                    ariaLabel={isPhoto ? t('compliance.ariaPhoto') : t('compliance.ariaCert')}
                    onChange={handleToggleEnabled}
                  />
                  <span>{enabled ? t('compliance.on') : t('compliance.off')}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="spo-batch-modal__hint">
                {t('compliance.agencyHint')}
              </p>

              <div className="spo-discuss-auto-rule-form">
                <label className="spo-field spo-field--full">
                  <span className="spo-field__label">{t('compliance.category')}</span>
                  <CategoryCascaderSelect
                    categories={categories}
                    value={draftCategoryId}
                    label={draftCategoryLabel}
                    disabled={disabled || metaLoading || !shopId}
                    placeholder={metaLoading ? t('compliance.categoryLoading') : t('compliance.categoryPh')}
                    emptyText={!shopId ? t('returnList.pickShop') : t('compliance.noCategory')}
                    includeAllOption
                    onChange={onCategoryChange}
                  />
                </label>

                <div className="spo-discuss-auto-rule-form__row">
                  <label className="spo-field">
                    <span className="spo-field__label">{t('compliance.agencyType')}</span>
                    <SearchableSelect
                      value={draftAgencyType}
                      disabled={disabled || metaLoading}
                      placeholder={t('compliance.pickAgencyType')}
                      options={agencyTypeOptions}
                      onChange={(value) => {
                        const id = String(value || '').trim();
                        setDraftAgencyType(id);
                        setDraftAgencyId('');
                        setDraftAgencyName('');
                      }}
                    />
                  </label>
                  <label className="spo-field">
                    <span className="spo-field__label">{t('compliance.agencyCompany')}</span>
                    <SearchableSelect
                      value={draftAgencyId}
                      disabled={disabled || metaLoading || !draftAgencyType}
                      placeholder={
                        !draftAgencyType
                          ? t('compliance.pickTypeShort')
                          : (metaLoading ? t('compliance.agencyLoading') : t('compliance.pickCompany'))
                      }
                      options={agencyCompanyOptions}
                      onChange={(value) => {
                        const id = String(value || '').trim();
                        const opt = agencyCompanyOptions.find((o) => o.value === id);
                        setDraftAgencyId(id);
                        setDraftAgencyName(opt?.label || '');
                      }}
                    />
                  </label>
                </div>

                <div className="spo-template-auto-rule__switch">
                  <SheinSwitch
                    checked={draftEnabled}
                    disabled={disabled}
                    ariaLabel={t('compliance.enableRule')}
                    onChange={setDraftEnabled}
                  />
                  <span>{t('compliance.defaultOn')}</span>
                </div>
                <button
                  type="button"
                  className="spo-btn spo-btn--secondary spo-btn--sm"
                  disabled={disabled}
                  onClick={addAgencyRule}
                >
                  {t('compliance.addRule')}
                </button>
              </div>

              <div className="spo-discuss-auto-rule-list">
                <div className="spo-discuss-auto-rule-list__head">{t('compliance.ruleHead', { count: rules.length })}</div>
                {loading ? (
                  <p className="spo-batch-modal__hint">{t('returnList.loading')}</p>
                ) : rules.length === 0 ? (
                  <p className="spo-batch-modal__hint">{t('compliance.noRule')}</p>
                ) : (
                  <ul className="spo-discuss-auto-rule-list__ul">
                    {rules.map((rule) => (
                      <li key={rule.id} className="spo-discuss-auto-rule-list__item">
                        <div className="spo-discuss-auto-rule-list__main">
                          <strong>{rule.categoryId === '*' ? t('productTemplate.allCategories') : (rule.categoryPathLabel || rule.categoryId)}</strong>
                          <span>
                            {agencyTypeLabel(rule.agencyType)}
                            {' · '}
                            {rule.agencyName || rule.agencyId}
                          </span>
                        </div>
                        <div className="spo-discuss-auto-rule-list__ops">
                          <SheinSwitch
                            checked={rule.enabled !== false}
                            disabled={disabled}
                            ariaLabel={t('compliance.enableOne')}
                            onChange={(next) => toggleRule(rule.id, next)}
                          />
                          <button
                            type="button"
                            className="spo-btn spo-btn--ghost spo-btn--sm"
                            disabled={disabled}
                            onClick={() => removeRule(rule.id)}
                          >
                            {t('returnList.delete')}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        <div className="spo-batch-modal__foot">
          {isSimple ? (
            <>
              <button type="button" className="spo-btn spo-btn--secondary" disabled={disabled} onClick={onClose}>
                {t('moduleShell.close')}
              </button>
              {enabled ? (
                <button
                  type="button"
                  className="spo-btn spo-btn--primary"
                  disabled={disabled || !shopId}
                  onClick={handleRunOnce}
                >
                  {running ? (isPhoto ? t('compliance.runPhoto') : t('compliance.binding')) : t('compliance.runNow')}
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button type="button" className="spo-btn spo-btn--secondary" disabled={disabled} onClick={onClose}>
                {t('shop.cancel')}
              </button>
              <button type="button" className="spo-btn spo-btn--secondary" disabled={disabled || !shopId} onClick={handleAgencySave}>
                {saving ? t('tiktokPublish.saving') : t('shop.save')}
              </button>
              <button type="button" className="spo-btn spo-btn--primary" disabled={disabled || !shopId} onClick={handleAgencyRun}>
                {running ? t('compliance.binding') : t('compliance.runBind')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
